import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { AdminLayout } from '../../components/AdminLayout';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Save, 
  X, 
  Image as ImageIcon, 
  Type, 
  Search,
  Loader2,
  Box,
  Layout,
  Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

export const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: '',
    image_url: ''
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const { data } = await api.services.getAll();
      setServices(data);
    } catch (error) {
      toast.error('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check requirements: Title OR Image must be present
    if (!formData.title && !formData.image_url) {
      toast.error('Debe proporcionar al menos un Título o una URL de Imagen');
      return;
    }

    try {
      if (editingId) {
        await api.services.update(editingId, formData);
        toast.success('Servicio actualizado');
      } else {
        await api.services.create(formData);
        toast.success('Servicio creado');
      }
      setIsModalOpen(false);
      setEditingId(null);
      setFormData({ title: '', description: '', icon: '', image_url: '' });
      fetchServices();
    } catch (error) {
      toast.error('Error al guardar el servicio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar este servicio?')) return;
    try {
      await api.services.delete(id);
      toast.success('Servicio eliminado');
      fetchServices();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openEdit = (service) => {
    setEditingId(service.id);
    setFormData({
      title: service.title || '',
      description: service.description || '',
      icon: service.icon || '',
      image_url: service.image_url || ''
    });
    setIsModalOpen(true);
  };

  return (
    <AdminLayout>
      <div className="space-y-12">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <span className="text-[#C6FF00] font-black uppercase tracking-[0.4em] text-[10px] block mb-2">Capabilities Hub</span>
            <h1 className="text-5xl font-black uppercase tracking-tight italic">Gestión de Servicios</h1>
          </div>
          <button 
            onClick={() => {
              setEditingId(null);
              setFormData({ title: '', description: '', icon: '', image_url: '' });
              setIsModalOpen(true);
            }}
            className="px-10 py-5 bg-[#C6FF00] text-black rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center gap-3 hover:scale-105 active:scale-95 transition-all shadow-[0_0_40px_rgba(198,255,0,0.2)]"
          >
            <Plus size={18} /> Nuevo Servicio
          </button>
        </header>

        {loading ? (
          <div className="flex items-center justify-center p-20">
            <Loader2 className="animate-spin text-[#C6FF00]" size={40} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence>
              {services.map((service) => (
                <motion.div
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  key={service.id}
                  className="p-10 rounded-[3rem] bg-white/[0.02] border border-white/5 hover:border-[#C6FF00]/40 transition-all group relative overflow-hidden"
                >
                  <div className="flex items-center justify-between mb-8">
                    {service.image_url ? (
                      <img src={service.image_url} alt={service.title} className="w-16 h-16 rounded-2xl object-cover border border-white/10" />
                    ) : (
                      <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10 text-[#C6FF00]">
                         <Box size={28} />
                      </div>
                    )}
                    <div className="flex gap-2">
                       <button onClick={() => openEdit(service)} className="p-3 bg-white/5 hover:bg-white/10 rounded-xl text-white/40 hover:text-white transition-all"><Edit2 size={16} /></button>
                       <button onClick={() => handleDelete(service.id)} className="p-3 bg-red-500/10 hover:bg-red-500 hover:text-white rounded-xl text-red-500 transition-all"><Trash2 size={16} /></button>
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-black uppercase italic mb-4 truncate">
                    {service.title || 'Módulo Nexus'}
                  </h3>
                  <p className="text-white/40 text-sm font-medium line-clamp-3 mb-8">
                    {service.description || 'Sin descripción técnica registrada.'}
                  </p>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-black/90 backdrop-blur-xl" 
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="w-full max-w-2xl bg-white/[0.02] border border-white/10 p-12 rounded-[4rem] relative z-10 overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C6FF00] to-transparent" />
              
              <div className="flex items-center justify-between mb-12">
                 <h2 className="text-2xl font-black uppercase italic">{editingId ? 'Editar Nodo' : 'Nuevo Nodo de Servicio'}</h2>
                 <button onClick={() => setIsModalOpen(false)} className="p-2 text-white/20 hover:text-white transition-colors"><X /></button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                   <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-white/40 ml-2 italic">Título (Opcional si hay imagen)</label>
                      <input 
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-sm font-black uppercase tracking-widest text-white focus:border-[#C6FF00]/40 outline-none"
                        placeholder="SOFTWARE AVANZADO"
                      />
                   </div>
                   <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-white/40 ml-2 italic">URL Imagen (Opcional si hay título)</label>
                      <input 
                        type="text"
                        value={formData.image_url}
                        onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-sm font-black uppercase tracking-widest text-white focus:border-[#C6FF00]/40 outline-none"
                        placeholder="https://..."
                      />
                   </div>
                </div>

                <div className="space-y-2">
                   <label className="text-[10px] font-black uppercase tracking-widest text-white/40 ml-2 italic">Descripción Técnica</label>
                   <textarea 
                     rows="4"
                     value={formData.description}
                     onChange={(e) => setFormData({...formData, description: e.target.value})}
                     className="w-full bg-white/5 border border-white/10 rounded-[2rem] p-8 text-sm font-medium text-white/60 focus:border-[#C6FF00]/40 outline-none resize-none"
                     placeholder="Detalla las capacidades y el valor del servicio..."
                   />
                </div>

                <div className="flex gap-4 pt-6">
                  <button 
                    type="submit"
                    className="flex-1 py-6 bg-[#C6FF00] text-black rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-3 hover:scale-105 active:scale-95 transition-all shadow-[0_0_50px_rgba(198,255,0,0.1)]"
                  >
                    <Save size={18} /> {editingId ? 'Actualizar Registro' : 'Inyectar Servicio'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
};
